import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import useCompanyGated from "../hooks/useCompanyGated";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Check, Plus, RotateCcw, MapPin, Loader2, MoreHorizontal, Link2, FileText, ChevronDown, ChevronUp, AlertTriangle, Zap } from "lucide-react";
import { FALLBACK_CATEGORIES, getCategoryIcon } from "../lib/categories";
const EMPLOYMENT_TYPES = ["Full-time", "Part-time", "Contract", "Internship"];
const WORK_ARRANGEMENTS = ["On-Site", "Hybrid", "Remote"];

export default function CompanyRoleNew() {
  const { isLocked } = useCompanyGated();
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [skills, setSkills] = useState("");
  const [category, setCategory] = useState("");
  const [customCategory, setCustomCategory] = useState("");

  useEffect(() => {
    api.get("/company/industries")
      .then(({ data }) => { if (data.length) setCategories(data); })
      .catch(() => {});
  }, []);
  const [location, setLocation] = useState("");
  const [employmentType, setEmploymentType] = useState("");
  const [workArrangement, setWorkArrangement] = useState("");
  const [salaryMin, setSalaryMin] = useState("");
  const [salaryMax, setSalaryMax] = useState("");
  const [salaryPeriod, setSalaryPeriod] = useState("yearly");
  const [endClient, setEndClient] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Import panel state
  const [importOpen, setImportOpen] = useState(true);
  const [importUrl, setImportUrl] = useState("");
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const [importWarning, setImportWarning] = useState("");
  const [importResolvedUrl, setImportResolvedUrl] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [locSuggestions, setLocSuggestions] = useState([]);
  const [locLoading, setLocLoading] = useState(false);
  const [showLocSuggestions, setShowLocSuggestions] = useState(false);
  const locDebounceRef = useRef(null);
  const locWrapperRef = useRef(null);
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [titleLoading, setTitleLoading] = useState(false);
  const [showTitleSuggestions, setShowTitleSuggestions] = useState(false);
  const titleDebounceRef = useRef(null);
  const titleWrapperRef = useRef(null);
  const navigate = useNavigate();

  const handleLocationInput = (val) => {
    setLocation(val);
    clearTimeout(locDebounceRef.current);
    if (val.trim().length < 2) { setLocSuggestions([]); setShowLocSuggestions(false); return; }
    setLocLoading(true);
    locDebounceRef.current = setTimeout(async () => {
      try {
        const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(val)}&limit=6&lang=en&layer=city&layer=district&layer=locality&layer=county`;
        const res = await fetch(url);
        const json = await res.json();
        const seen = new Set();
        const items = (json.features || [])
          .map((f) => { const p = f.properties; return [p.name, p.state, p.country].filter(Boolean).join(", "); })
          .filter((label) => seen.has(label) ? false : seen.add(label));
        setLocSuggestions(items);
        setShowLocSuggestions(items.length > 0);
      } catch { setLocSuggestions([]); }
      finally { setLocLoading(false); }
    }, 300);
  };

  const pickLocation = (label) => {
    setLocation(label);
    setLocSuggestions([]);
    setShowLocSuggestions(false);
  };

  const handleTitleInput = (val) => {
    setTitle(val);
    clearTimeout(titleDebounceRef.current);
    if (val.trim().length < 2) { setTitleSuggestions([]); setShowTitleSuggestions(false); return; }
    setTitleLoading(true);
    titleDebounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/roles/title-search", { params: { q: val.trim() } });
        const items = (data || []).map((r) => r.name || r);
        setTitleSuggestions(items);
        setShowTitleSuggestions(items.length > 0);
      } catch { setTitleSuggestions([]); }
      finally { setTitleLoading(false); }
    }, 300);
  };

  const pickTitle = (name) => {
    setTitle(name);
    setTitleSuggestions([]);
    setShowTitleSuggestions(false);
  };

  const currentSkills = skills.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
  const isAdded = (skill) => currentSkills.includes(skill.toLowerCase());

  const toggleSkill = (skill) => {
    const existing = skills.split(",").map((s) => s.trim()).filter(Boolean);
    if (isAdded(skill)) {
      setSkills(existing.filter((s) => s.toLowerCase() !== skill.toLowerCase()).join(", "));
    } else {
      setSkills(existing.length ? existing.join(", ") + ", " + skill : skill);
    }
  };

  const suggestSkills = async () => {
    if (!desc.trim() || suggesting) return;
    setSuggesting(true);
    setSuggestions([]);
    try {
      const { data } = await api.post("/jobs/suggest-skills", { description: desc });
      setSuggestions(data.skills || []);
      if (!data.skills?.length) toast.info("No skills detected — try a more detailed description");
    } catch {
      toast.error("Could not fetch suggestions");
    } finally {
      setSuggesting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const finalCategory = category === "Other" && customCategory.trim() ? customCategory.trim() : category;
    if (!finalCategory) { toast.error("Please select a category"); return; }
    setSubmitting(true);
    try {
      const required_skills = skills.split(",").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post("/company/roles", {
        title, description: desc, required_skills,
        category: finalCategory,
        location: location.trim() || undefined,
        employment_type: employmentType || undefined,
        work_arrangement: workArrangement || undefined,
        salary_min: salaryMin ? parseFloat(salaryMin) : undefined,
        salary_max: salaryMax ? parseFloat(salaryMax) : undefined,
        salary_period: (salaryMin || salaryMax) ? salaryPeriod : undefined,
        end_client: endClient.trim() || undefined,
      });
      if (category === "Other" && customCategory.trim()) {
        api.post("/company/industries/suggest", { name: customCategory.trim() }).catch(() => {});
      }
      toast.success("Role created");
      navigate(`/company/roles/${data.company_role_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create role");
    } finally { setSubmitting(false); }
  };

  const handleImport = async () => {
    if (!importUrl.trim() && !importText.trim()) {
      toast.error("Enter a URL or paste the job description text");
      return;
    }
    setImporting(true);
    setImportWarning("");
    setImportResolvedUrl("");
    try {
      const { data } = await api.post("/company/roles/parse-posting", {
        url: importUrl.trim() || undefined,
        text: importText.trim() || undefined,
      });
      // URL hard-blocked with no fallback text — show actionable warning, stop here
      if (!data.ok && data.url_blocked) {
        setImportWarning(data.message);
        setImportResolvedUrl(data.resolved_url || "");
        return;
      }
      const f = data.fields;
      let filledCount = 0;
      if (f.title)              { setTitle(f.title);                           filledCount++; }
      if (f.end_client)         { setEndClient(f.end_client);                  filledCount++; }
      if (f.description)        { setDesc(f.description); setSuggestions([]);  filledCount++; }
      if (f.employment_type)    { setEmploymentType(f.employment_type);        filledCount++; }
      if (f.work_arrangement)   { setWorkArrangement(f.work_arrangement);      filledCount++; }
      if (f.location)           { setLocation(f.location);                     filledCount++; }
      if (f.salary_min != null) { setSalaryMin(String(f.salary_min));          filledCount++; }
      if (f.salary_max != null) { setSalaryMax(String(f.salary_max));          filledCount++; }
      if (f.salary_period)      { setSalaryPeriod(f.salary_period);            filledCount++; }
      if (f.required_skills?.length) { setSkills(f.required_skills.join(", ")); filledCount++; }
      if (f.category) {
        if (categories.includes(f.category)) {
          setCategory(f.category);
          setCustomCategory("");
        } else {
          setCategory("Other");
          setCustomCategory(f.category);
        }
        filledCount++;
      }

      // URL was blocked but paste text succeeded — show warning inline, don't close panel
      if (data.url_blocked && data.message) {
        setImportWarning(data.message);
        setImportResolvedUrl(data.resolved_url || "");
      }

      if (filledCount === 0) {
        // Nothing useful extracted — keep panel open with a clear message
        setImportWarning(
          "Couldn't extract job details from this URL. " +
          "Open the link below, copy all the text from the page, and paste it in the box above."
        );
        setImportResolvedUrl(data.resolved_url || importUrl.trim());
        return;
      }

      setImportOpen(false);
      if (filledCount < 3) {
        toast.warning(`Partially filled (${filledCount} field${filledCount > 1 ? "s" : ""}) — fill in the rest manually`);
      } else {
        toast.success("Form filled from job posting — review and adjust before saving");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed — try pasting the text instead");
    } finally {
      setImporting(false);
    }
  };

  return (
    <main className="max-w-4xl mx-auto px-6 md:px-12 py-12">
      <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground mb-3">
        <Link to="/company/roles" className="hover:text-foreground">ROLES</Link>
        <span>/</span>
        <span>NEW</span>
      </div>
      <h1 className="font-display font-black text-4xl tracking-tight text-[#0F172A] mb-6">Post a role.</h1>

      {/* Import banner */}
      <div className="rounded-xl border border-[#0025D8]/30 bg-[#0025D8]/3 mb-6">
        <button
          type="button"
          onClick={() => setImportOpen((o) => !o)}
          className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-[#0025D8]/5 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Zap className="w-3.5 h-3.5 text-[#0025D8]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0025D8] font-semibold">Import from job posting</span>
            <span className="font-mono text-[10px] text-muted-foreground">— paste from LinkedIn, Indeed, Glassdoor, or any source</span>
          </div>
          {importOpen ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
        </button>

        {importOpen && (
          <div className="px-5 pb-5 space-y-3 border-t border-[#0025D8]/20">
            {/* URL input */}
            <div className="pt-4">
              <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Job posting URL</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                  <Input
                    className="rounded-xl pl-9"
                    value={importUrl}
                    onChange={(e) => { setImportUrl(e.target.value); setImportWarning(""); }}
                    placeholder="https://www.linkedin.com/jobs/view/... or indeed.com/..."
                  />
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 border-t border-border" />
              <span className="font-mono text-[10px] text-muted-foreground">or paste job description text</span>
              <div className="flex-1 border-t border-border" />
            </div>

            {/* Paste area */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                <Label className="uppercase tracking-wider text-xs font-semibold">Job Description Text</Label>
              </div>
              <Textarea
                className="rounded-xl min-h-[120px] font-mono text-xs"
                value={importText}
                onChange={(e) => { setImportText(e.target.value); setImportWarning(""); }}
                placeholder="Copy and paste the full job posting here — title, responsibilities, requirements, skills, salary…"
              />
            </div>

            {/* Warning (URL blocked) */}
            {importWarning && (
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-[#F59E0B]/10 border border-[#F59E0B]/30">
                <AlertTriangle className="w-3.5 h-3.5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                <div className="space-y-1.5">
                  <p className="font-mono text-[10px] text-[#92400E]">{importWarning}</p>
                  {importResolvedUrl && (
                    <a
                      href={importResolvedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-mono text-[10px] text-[#0025D8] underline hover:no-underline"
                    >
                      <Link2 className="w-3 h-3" />
                      Open job posting →
                    </a>
                  )}
                </div>
              </div>
            )}

            <Button
              type="button"
              onClick={handleImport}
              disabled={importing || (!importUrl.trim() && !importText.trim())}
              className="rounded-xl bg-[#0025D8] hover:bg-[#001CB3] px-6"
            >
              {importing ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />Extracting…</> : <><Sparkles className="w-3.5 h-3.5 mr-2" />Extract &amp; fill form</>}
            </Button>
          </div>
        )}
      </div>

      <form onSubmit={submit} className="space-y-8 rounded-xl border border-border p-8 bg-white">

        {/* Title + End Client + Employment Type */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <div>
              <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Job Title *</Label>
              <div className="relative" ref={titleWrapperRef}>
                {titleLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin pointer-events-none z-10" />}
                <Input
                  className="rounded-xl py-6"
                  required
                  value={title}
                  onChange={(e) => handleTitleInput(e.target.value)}
                  onFocus={() => titleSuggestions.length > 0 && setShowTitleSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowTitleSuggestions(false), 150)}
                  placeholder="Senior Backend Engineer"
                  autoComplete="off"
                />
                {showTitleSuggestions && titleSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 z-50 rounded-xl border border-border bg-white shadow-lg mt-0.5">
                    {titleSuggestions.map((name) => (
                      <button
                        key={name}
                        type="button"
                        onMouseDown={() => pickTitle(name)}
                        className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#0025D8]/5 hover:text-[#0025D8] transition-colors border-b border-border/50 last:border-0"
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">End Client</Label>
              <Input
                className="rounded-xl py-6"
                value={endClient}
                onChange={(e) => setEndClient(e.target.value)}
                placeholder="e.g. Royal Bank of Canada"
              />
              <p className="font-mono text-[10px] text-muted-foreground mt-1.5">Optional — the company you're placing this role with</p>
            </div>
          </div>
          <div>
            <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Employment Type</Label>
            <div className="flex flex-wrap gap-2 pt-1">
              {EMPLOYMENT_TYPES.map((t) => (
                <button key={t} type="button" onClick={() => setEmploymentType(employmentType === t ? "" : t)}
                  className={`font-mono text-[10px] px-2.5 py-1 border transition-colors ${employmentType === t ? "bg-[#0F172A] text-white border-[#0A0A0A]" : "text-muted-foreground border-border hover:border-foreground hover:text-foreground"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Category */}
        <div>
          <Label className="uppercase tracking-wider text-xs font-semibold mb-3 block">Category *</Label>
          <div className="flex flex-wrap gap-2">
            {[...categories, "Other"].map((cat) => {
              const Icon = getCategoryIcon(cat);
              return (
                <button key={cat} type="button" onClick={() => setCategory(cat)}
                  className={`inline-flex items-center gap-1.5 font-mono text-[11px] px-3 py-1.5 border font-semibold transition-all duration-150 ${
                    category === cat
                      ? "bg-[#0025D8] text-white border-[#0025D8]"
                      : "text-muted-foreground border-border hover:border-[#0025D8] hover:text-[#0025D8]"
                  }`}>
                  <Icon className="w-3 h-3 flex-shrink-0" />
                  {cat}
                </button>
              );
            })}
          </div>
          {category === "Other" && (
            <div className="mt-3 space-y-1">
              <Input
                className="rounded-xl max-w-xs"
                placeholder="e.g. AgriTech, EdTech, CleanEnergy…"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
              />
              <p className="font-mono text-[10px] text-[#F59E0B]">This will be sent to admin as a suggestion to add as an industry.</p>
            </div>
          )}
        </div>

        {/* Location */}
        <div>
          <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Location</Label>
          <div className="relative max-w-sm" ref={locWrapperRef}>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              {locLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin pointer-events-none" />}
              <Input
                className="rounded-xl py-6 pl-9"
                value={location}
                onChange={(e) => handleLocationInput(e.target.value)}
                onFocus={() => locSuggestions.length > 0 && setShowLocSuggestions(true)}
                onBlur={() => setTimeout(() => setShowLocSuggestions(false), 150)}
                placeholder="Remote · New York, NY · London"
                autoComplete="off"
              />
            </div>
            {showLocSuggestions && locSuggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 z-50 rounded-xl border border-border bg-white shadow-lg mt-0.5">
                {locSuggestions.map((label) => (
                  <button
                    key={label}
                    type="button"
                    onMouseDown={() => pickLocation(label)}
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#0025D8]/5 hover:text-[#0025D8] transition-colors flex items-center gap-2 border-b border-border/50 last:border-0"
                  >
                    <MapPin className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Work Arrangement + Salary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Work Arrangement</Label>
            <div className="flex flex-wrap gap-2 pt-1">
              {WORK_ARRANGEMENTS.map((t) => (
                <button key={t} type="button" onClick={() => setWorkArrangement(workArrangement === t ? "" : t)}
                  className={`font-mono text-[10px] px-2.5 py-1 border transition-colors ${workArrangement === t ? "bg-[#0F172A] text-white border-[#0A0A0A]" : "text-muted-foreground border-border hover:border-foreground hover:text-foreground"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="uppercase tracking-wider text-xs font-semibold">Salary Range</Label>
              <div className="flex items-center rounded-xl border border-border overflow-hidden">
                {["yearly", "hourly"].map((p) => (
                  <button key={p} type="button" onClick={() => setSalaryPeriod(p)}
                    className={`font-mono text-[10px] px-2.5 py-1 transition-colors ${salaryPeriod === p ? "bg-[#0025D8] text-white" : "text-muted-foreground hover:text-foreground"}`}>
                    {p === "yearly" ? "/yr" : "/hr"}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Input
                className="rounded-xl"
                type="number"
                min="0"
                value={salaryMin}
                onChange={(e) => setSalaryMin(e.target.value)}
                placeholder="Min"
              />
              <span className="text-muted-foreground text-sm flex-shrink-0">–</span>
              <Input
                className="rounded-xl"
                type="number"
                min="0"
                value={salaryMax}
                onChange={(e) => setSalaryMax(e.target.value)}
                placeholder="Max"
              />
            </div>
            <p className="font-mono text-[10px] text-muted-foreground mt-1.5">Optional — leave blank to not display</p>
          </div>
        </div>

        {/* Description */}
        <div>
          <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Job Description *</Label>
          <Textarea
            className="rounded-xl min-h-[220px]"
            required
            value={desc}
            onChange={(e) => { setDesc(e.target.value); setSuggestions([]); }}
            placeholder="What the role does, must-haves, nice-to-haves…"
          />
        </div>

        {/* Required Skills */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="uppercase tracking-wider text-xs font-semibold">Required Skills (comma separated)</Label>
            <button
              type="button"
              onClick={suggestSkills}
              disabled={!desc.trim() || suggesting}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border transition-all duration-150 ${
                desc.trim() && !suggesting
                  ? "bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/50 hover:bg-[#7C3AED] hover:text-white hover:border-[#7C3AED]"
                  : "bg-secondary text-muted-foreground border-border cursor-not-allowed opacity-50"
              }`}>
              <Sparkles className={`w-3.5 h-3.5 ${suggesting ? "animate-pulse" : ""}`} />
              {suggesting ? "Detecting…" : "Suggest from description"}
            </button>
          </div>
          <Input
            className="rounded-xl py-6"
            value={skills}
            onChange={(e) => setSkills(e.target.value)}
            placeholder="Python, FastAPI, MongoDB, AWS"
          />
          {suggestions.length > 0 && (
            <div className="mt-3">
              <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mb-2">SUGGESTED — click to add or remove</div>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((skill) => {
                  const added = isAdded(skill);
                  return (
                    <button key={skill} type="button" onClick={() => toggleSkill(skill)}
                      className={`flex items-center gap-1.5 px-3 py-1 text-xs font-semibold border transition-all duration-150 ${
                        added ? "bg-[#0025D8] text-white border-[#0025D8]" : "bg-[#0025D8]/8 text-[#0025D8] border-[#0025D8]/40 hover:bg-[#0025D8] hover:text-white hover:border-[#0025D8]"
                      }`}>
                      {added ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                      {skill}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4 pt-2">
          <Button
            type="submit"
            disabled={submitting || !category || isLocked}
            title={isLocked ? "Activate your account to create roles" : undefined}
            className="rounded-xl bg-[#0025D8] hover:bg-[#001CB3] px-8 py-6 text-base disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? "Creating…" : "Create role →"}
          </Button>
          <Link to="/company/roles" className="text-sm text-muted-foreground hover:text-foreground font-mono">Cancel</Link>
        </div>
      </form>
    </main>
  );
}
